import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Box, Button, Typography } from "@mui/material";
import { Link } from "react-router";

import { NOTIFICATION_DELIVERY } from "@/context/notificationConstants";
import { useNotification, useStore } from "@/hooks";
import { devDebugLog } from "@/utils/devDebugLog";

import {
  augmentProcessNotificationMessageWithStreet,
  resolvePropertyRecordForNotification,
} from "./augmentMessageWithPropertyStreet";
import { isAllowedProcessNotificationRoute } from "./isAllowedProcessNotificationRoute";

const ProcessNotificationBridge = observer(() => {
  const { notify } = useNotification();
  const { processTrackerStore, authStore, propertyDetailsStore, propertyListStore } = useStore();
  const notification = processTrackerStore?.pendingNotifications?.[0] ?? null;

  useEffect(() => {
    if (!notification) return;

    const dismiss = () => {
      processTrackerStore.consumeNotification();
    };

    const routeRaw = notification.actionRoute;
    const safeActionRoute =
      typeof routeRaw === "string" && isAllowedProcessNotificationRoute(routeRaw) ? routeRaw : null;
    if (typeof routeRaw === "string" && routeRaw && !safeActionRoute) {
      devDebugLog("[ProcessNotificationBridge]", "ignoredActionRoute", { route: routeRaw });
    }

    const propertyRecord = resolvePropertyRecordForNotification(
      propertyDetailsStore,
      propertyListStore,
      authStore?.customer ?? "",
      notification.propertyId
    );
    const displayMessage = augmentProcessNotificationMessageWithStreet(notification.message, propertyRecord);

    notify.rich(
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.65 }}>
        <Typography sx={{ fontSize: "1.125rem", fontWeight: 500, lineHeight: 1.38 }}>{displayMessage}</Typography>
        {safeActionRoute ? (
          <Button
            component={Link}
            to={safeActionRoute}
            size="small"
            variant="outlined"
            color="inherit"
            sx={{ textTransform: "none", minWidth: "fit-content", px: 1.25, fontSize: "1.059rem" }}
            onClick={dismiss}
            onAuxClick={(e) => {
              if (e.button === 1) dismiss();
            }}
          >
            {notification.actionLabel || "View"}
          </Button>
        ) : null}
      </Box>,
      notification.outcome === "error"
        ? "error"
        : notification.outcome === "warning"
          ? "warning"
          : notification.outcome === "info"
            ? "info"
            : "success",
      {
        onDismiss: dismiss,
        delivery: NOTIFICATION_DELIVERY.SNACKBAR_AND_INBOX,
        inboxMessage: displayMessage,
        inboxUnread: true,
        actionRoute: typeof notification.actionRoute === "string" ? notification.actionRoute : undefined,
        actionLabel: typeof notification.actionLabel === "string" ? notification.actionLabel : undefined,
      }
    );
  }, [authStore, notification, notify, processTrackerStore, propertyDetailsStore, propertyListStore]);

  return null;
});

export default ProcessNotificationBridge;
